import { clsx } from "clsx";
import pluralize from "pluralize";
import { type FC, memo, useCallback, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { HeaderDivider, LoadingSpinner, SymbolIcon, TriggerButton } from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { useURLViewID } from "@/app-utils/hooks/useUrl.ts";
import type { InputRestriction, InputTableQuerySpec, Organism, VariantAnalysisGroup } from "@/graphql.ts";
import {
  featuresTableHideStateChanged,
  type IGenomeBrowserState,
  selectActiveFeaturesTable,
} from "@/redux/modules/genome-browser.ts";
import { createUniqueKeyFromTableSpec, getTableDataSlice } from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { emptyAnalysisGroup, ENTITY_TYPE, VIEW } from "@/settings/settings.ts";
import { useGenomicTableRestrictions } from "@/ui-modules/genome-viewer/hooks/useGenomicTableCollections.ts";
import { SyncMutantGenomesButton } from "@/ui-modules/genome-viewer/SyncMutantGenomesButton.tsx";

import ButtonHeader from "../../core/ButtonHeader.js";
import { ContentPlaceholder } from "../../core/ContentPlaceholder.js";
import PageHeader from "../../core/navigation-panel/PageHeader.js";
import style from "../styles/GenomicFeatureTable.module.scss";
import DataTableRestrictions, { type DataTableRestrictionsProps } from "./DataTableRestrictions.js";
import { MUTANT_GENOMES_COLLECTION_NAME } from "./GenomicFeatureTable.js";
import { TableRestrictionsDropdown } from "./TableRestrictionsDropdown.js";

/**
 * Encapsulated information used to render tabbed tables.
 */
export interface ITabbedTableCollection extends DataTableRestrictionsProps {
  id: any;
  name: string; // e.g. saved group has a specific name
  type?: string;
  color?: string;
  showColorBadge?: boolean;
}

const COLUMNS_BUTTON_PORTAL_CONTEXT = "genomicFeatureTable";

interface IFeaturesTableToolbarProps {
  collections: ITabbedTableCollection[];
  selectedCollection: ITabbedTableCollection;
  verticalLayout: boolean;
}

const getFeaturesTableTabKey = (collection: ITabbedTableCollection) =>
  collection && createUniqueKeyFromTableSpec(collection.tableSpec);

const FeaturesTableToolbar: FC<IFeaturesTableToolbarProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const genome = useSelector<IAppState, Organism>((state) => state.genomeBrowser.genome);
  const { featuresTableLayout } = useSelector<IAppState, IGenomeBrowserState>(
    (state) => state.genomeBrowser,
    shallowEqual,
  );

  const menuOptionRenderer = useCallback((collectionDescription: ITabbedTableCollection) => {
    return <FeaturesTableCollectionOption collection={collectionDescription}></FeaturesTableCollectionOption>;
  }, []);

  return (
    <PageHeader
      title={""}
      tempNoOverflow
      toolsWidgets={[
        props.collections && props.collections.length != 0 && (
          <div
            style={{ width: props.verticalLayout ? "300px" : "200px" }}
            key={"genome-viewer-tabbed-table-view-widgets-1"}
          >
            <Select
              className={"react-select"}
              classNamePrefix={"react-select"}
              isMulti={false}
              value={props.selectedCollection}
              getOptionValue={getFeaturesTableTabKey}
              isClearable={false}
              isSearchable={false}
              options={props.collections}
              formatOptionLabel={menuOptionRenderer}
              onChange={(collectionDescription: ITabbedTableCollection) =>
                dispatch(selectActiveFeaturesTable(collectionDescription.tableSpec))
              }
              closeMenuOnSelect={true}
              openMenuOnFocus={false}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 650,
                }),
                menu: (styles) => ({ ...styles, width: "max-content", minWidth: "100%" }), // allows the dropdown to use the full width driven by the content
              }}
            />
          </div>
        ),
        props.selectedCollection?.enableTableRestrictions ? (
          <TableRestrictionsDropdown
            activeTableSpec={props.selectedCollection?.tableSpec}
            key={"genome-viewer-tabbed-table-view-widgets-2"}
          />
        ) : null,
      ]}
      actionWidgets={
        genome && [
          props.selectedCollection?.tableSpec?.queryType === ENTITY_TYPE.ANALYSIS_GROUP ? (
            <SyncMutantGenomesButton tableSpec={props.selectedCollection?.tableSpec} />
          ) : null,
          <ButtonHeader
            viewType={VIEW.TABLE_VIEW.type}
            tableSpec={props.selectedCollection?.tableSpec}
            getParentEntityName={() => genome.name}
            hideBlastBtn={true}
            hideShowRelatedBtn={props.selectedCollection?.name === MUTANT_GENOMES_COLLECTION_NAME} // special case for the listing of mutant genomes
            hideExportBtn={false}
            hideSaveBtn={true}
            portalContext={COLUMNS_BUTTON_PORTAL_CONTEXT}
            key={"genome-viewer-tabbed-table-view-action-1"}
          />,
          <HeaderDivider key={"genome-viewer-tabbed-table-view-action-2"} />,
          <TriggerButton
            tooltip={"Hide Features Table"}
            shortcut={"Shift+T"}
            icon={
              <SymbolIcon
                icon={featuresTableLayout.verticalLayout ? "bottom_panel_close" : "right_panel_close"}
                filled
              />
            }
            onPress={() => {
              dispatch(
                featuresTableHideStateChanged({
                  ...featuresTableLayout,
                  hidden: true,
                }),
              );
            }}
            key={"genome-viewer-tabbed-table-view-action-3"}
            label={"Hide"}
          />,
        ]
      }
    />
  );
};

interface FeaturesTableCollectionOptionProps {
  collection: ITabbedTableCollection;
}

const FeaturesTableCollectionOption: FC<FeaturesTableCollectionOptionProps> = (props) => {
  const { name, type, tableSpec } = props.collection;
  const totalCount = useSelector<IAppState, number>(
    (state) => getTableDataSlice(state.tableData, tableSpec)?.totalRowCount,
  );
  const restrictedCount = useSelector<IAppState, number>(
    (state) => getTableDataSlice(state.tableData, tableSpec)?.restrictedRowCount,
  );

  let subTitle = "";
  if (totalCount > 0) {
    const totalCountLabel = formatNumberByThousands(totalCount);
    const restrictedCountLabel = formatNumberByThousands(restrictedCount);
    subTitle = restrictedCount < totalCount ? `(${restrictedCountLabel}/${totalCountLabel})` : `(${totalCountLabel})`;
  }

  return (
    <span className={style.table_dropdown_option} title={`${pluralize.plural(name)} ${subTitle}`}>
      <span>
        <span className={style.table_feature_option_title}>{pluralize.plural(name)} </span>
        <span className={style.table_feature_option_subtitle}>{subTitle} </span>
        {type && <span className={style.table_feature_option_type}>[{type}]</span>}
      </span>
    </span>
  );
};

interface IFeaturesTablePaneProps {
  isLoading?: boolean;
  collections: ITabbedTableCollection[];
  selectedCollection: ITabbedTableCollection;
  variantAnalysisGroup?: VariantAnalysisGroup; // for ref genome drop down
}

const FeaturesTablePane: FC<IFeaturesTablePaneProps> = memo<IFeaturesTablePaneProps>((props) => {
  const genome = useSelector<IAppState, Organism>((state) => state.genomeBrowser.genome);
  const refGenomeId = useSelector<IAppState, number>((state) => state.auth.session?.params.referenceGenome);
  const genomeIsLoading = useSelector<IAppState, boolean>((state) => state.genomeBrowser.isLoading);
  const tableRestrictions = useGenomicTableRestrictions();

  if (!genome) {
    return (
      <ContentPlaceholder key="tabbed-table-view-placeholder-text">
        Please select a genome to visualize
      </ContentPlaceholder>
    );
  }

  if (!props.collections.length) {
    return <ContentPlaceholder key="tabbed-table-view-placeholder-text">No track data to show</ContentPlaceholder>;
  }

  if (props.isLoading) {
    return <LoadingSpinner key="tabbed-table-view-spinner" inCenter />;
  }

  const activeTabKey = getFeaturesTableTabKey(props.selectedCollection);

  // this is gonna be used only for non VCF variants grids
  const variantAnalysisGroup: VariantAnalysisGroup =
    !genomeIsLoading && genome && (genome.variantAnalysisGroups?.[0] || emptyAnalysisGroup);

  return (
    <div className={style.tab_container}>
      {props.collections.map((collectionDescription: ITabbedTableCollection) => {
        const tabKey = getFeaturesTableTabKey(collectionDescription);
        return (
          <GenomeViewerGrid
            key={tabKey}
            collectionDescription={collectionDescription}
            isActive={activeTabKey === tabKey}
            tableRestrictions={tableRestrictions}
            variantAnalysisGroup={variantAnalysisGroup}
            refGenomeId={refGenomeId}
          />
        );
      })}
    </div>
  );
});

interface GenomeViewerGridProps {
  collectionDescription: ITabbedTableCollection;
  tableRestrictions: InputRestriction;
  isActive: boolean;
  refGenomeId: number; // used only for non VCF variants grids
  variantAnalysisGroup: VariantAnalysisGroup; // used only for non VCF variants grids
}

const GenomeViewerGrid: FC<GenomeViewerGridProps> = memo<GenomeViewerGridProps>((props) => {
  const collectionDescription = props.collectionDescription;
  const tabKey = getFeaturesTableTabKey(collectionDescription);
  const viewId = useURLViewID();

  const [tableSpecWithRestrictions, setTableSpecWithRestrictions] = useState<InputTableQuerySpec>(null);

  useEffect(() => {
    if (collectionDescription.tableSpec) {
      // update the existing table query spec with up to date restrictions
      let tableSpec: InputTableQuerySpec = {
        ...collectionDescription.tableSpec,
      };

      if (collectionDescription.tableSpec.queryType !== ENTITY_TYPE.ANALYSIS_GROUP) {
        // other browsing restrictions does not make sense
        // for listing the mutant genomes of the analysis group
        tableSpec.restrictions = { ...tableSpec.restrictions, ...props.tableRestrictions };
      }

      switch (tableSpec.resultType) {
        case ENTITY_TYPE.MUTATION:
        case ENTITY_TYPE.VARIANT: {
          if (props.variantAnalysisGroup?.refGenomes?.length) {
            const referenceGenomes: Organism[] = props.variantAnalysisGroup.refGenomes;
            if (props.refGenomeId && !!referenceGenomes.find((rg: Organism) => rg.id === props.refGenomeId)) {
              tableSpec.restrictions.refGenomeId = props.refGenomeId;
            } else {
              tableSpec = null; // so that we do not trigger a request with incomplete or wrong data
            }
          }
        }
      }
      setTableSpecWithRestrictions(tableSpec);
    }
  }, [collectionDescription.tableSpec, props.tableRestrictions, props.variantAnalysisGroup, props.refGenomeId]);

  return (
    <div
      aria-hidden={!props.isActive}
      data-testid={props.isActive ? "visible-tab" : "hidden-tab"}
      className={clsx({ [style.hidden_container]: !props.isActive })}
      key={`${tabKey}-container`}
    >
      {tableSpecWithRestrictions && ( // variant table might not be ready in regards to the ref genome id logic
        <DataTableRestrictions
          key={tabKey}
          onFeatureFocused={collectionDescription.onFeatureFocused}
          inactive={!props.isActive}
          tableSpec={tableSpecWithRestrictions}
          viewId={viewId}
          hideCheckboxes={collectionDescription.hideCheckboxes}
          enableTableRestrictions={collectionDescription.tableSpec.resultType !== ENTITY_TYPE.ORGANISM}
          portalContext={COLUMNS_BUTTON_PORTAL_CONTEXT}
          preventReloadOfSavedView={true} // set directly here because TabbedTableView is only used in context of the Genome Browser
          onCellMouseOver={collectionDescription.onCellMouseOver}
          onCellMouseOut={collectionDescription.onCellMouseOut}
          tableInitializationOptions={{
            sortExpression:
              // set artificially the sorting specifically in the context of the genome viewer
              collectionDescription.tableSpec.resultType !== ENTITY_TYPE.ORGANISM && // for performance reasons
              collectionDescription.tableSpec.resultType !== ENTITY_TYPE.VARIANT && // for performance reasons
              collectionDescription.tableSpec.resultType !== ENTITY_TYPE.VCF_VARIANT && // for performance reasons
              collectionDescription.tableSpec.resultType !== ENTITY_TYPE.AAFEATURE // region is relative to the protein, does not make sense to sort by region
                ? "region.contig.sequenceId,region.range.start,region.range.stop"
                : null,
          }}
          noRowsText={
            collectionDescription.tableSpec.resultType === ENTITY_TYPE.REGION_FLAG &&
            "You do not have any Genome Flags yet"
          }
        />
      )}
    </div>
  );
});

export { FeaturesTablePane, FeaturesTableToolbar };
